import { GuestRoute } from '@dealstack/frontend/shared/pages/GuestRoute/GuestRoute';
import {
	AUTH_ROUTES,
	DEFAULT_GUEST_ROUTE,
	DEFAULT_PROTECTED_ROUTE,
	LEGACY_AUTH_ROUTES,
	ROUTES,
} from '@dealstack/frontend/investor/pages/Routing/routes';
import { LinkExpiredNotice } from '@dealstack/frontend/shared/pages/LinkExpiredNotice/LinkExpiredNotice';
import { LegacyInvitationLink } from '@dealstack/frontend/shared/pages/LinkExpiredNotice/LegacyInvitationLink';
import { ApplicationType } from '@dealstack/shared/lib/generated';
import { Navigate, Route } from 'react-router-dom';
import { AuthLayoutV2 } from '@dealstack/frontend/shared/layout/AuthLayout/AuthLayoutV2';
import { AuthStart } from '@dealstack/frontend/shared/pages/AuthStart/AuthStart';
import { InvestorPhone } from '@dealstack/frontend/shared/pages/InvestorPhone/InvestorPhone';
import { CancelEmailChange } from '@dealstack/frontend/shared/pages/CancelEmailChange/CancelEmailChange';
import { InvitationExpired } from '@dealstack/frontend/shared/pages/InvitationExpired/InvitationExpired';
import { SentryRoutes } from '@dealstack/frontend/shared/sentry';

interface Props {
	className?: string;
	applicationType: ApplicationType;
}

export const Auth = ({ className, applicationType }: Props) => {
	const guestRoutes = (
		<GuestRoute defaultProtectedRoute={DEFAULT_PROTECTED_ROUTE}>
			<SentryRoutes>
				<Route
					path={AUTH_ROUTES.LOGIN}
					element={<AuthStart applicationType={applicationType} />}
				/>
				{/* Before the catch-all, which would drop the token and the explanation with it. */}
				<Route
					path={LEGACY_AUTH_ROUTES.RESET_PASSWORD}
					element={
						<LinkExpiredNotice kind="password-reset" loginRoute={DEFAULT_GUEST_ROUTE} />
					}
				/>
				<Route path="*" element={<Navigate to={`${ROUTES.AUTH}${AUTH_ROUTES.LOGIN}`} />} />
			</SentryRoutes>
		</GuestRoute>
	);

	return (
		<div className={className}>
			<AuthLayoutV2>
				<SentryRoutes>
					{/* Outside GuestRoute: the cancel link goes to the account owner, who is
					    usually still signed in, and GuestRoute would bounce them to the dashboard. */}
					<Route
						path={AUTH_ROUTES.CANCEL_EMAIL_CHANGE}
						element={<CancelEmailChange loginRoute={DEFAULT_GUEST_ROUTE} />}
					/>
					{/* Outside GuestRoute for the same reason: the callback diverts here only
					    after startUserSession, so GuestRoute would bounce past the step. */}
					<Route
						path={AUTH_ROUTES.REGISTER}
						element={
							<InvestorPhone
								applicationType={applicationType}
								onSuccessRedirect={DEFAULT_PROTECTED_ROUTE}
							/>
						}
					/>
					<Route
						path={AUTH_ROUTES.INVITATION_EXPIRED}
						element={<InvitationExpired applicationType={applicationType} />}
					/>
					{/* TODO(ENT-769): remove after 2027-04-03, six months after the last
					    legacy-style invitation email (2026-10-03). */}
					<Route
						path={LEGACY_AUTH_ROUTES.ACCEPT_INVITE}
						element={
							<LegacyInvitationLink
								applicationType={applicationType}
								loginRoute={DEFAULT_GUEST_ROUTE}
							/>
						}
					/>
					<Route path="*" element={guestRoutes} />
				</SentryRoutes>
			</AuthLayoutV2>
		</div>
	);
};
